import {Link, useRouteContext} from '@tanstack/react-router'
import {Suspense, useCallback, useState} from 'react'
import {useReducedMotion} from '@react-spring/web'
import {useQuery} from '@tanstack/react-query'
import {useTranslations} from 'use-intl'
import {useReward} from 'react-rewards'
import clsx from 'clsx'
import {
    UpsellingProducts,
    UpsellingProductsSkeletonLoader,
} from '~/components/basket/UpsellingProducts'
import {basketPageContentQueryOptions} from '~/client/fn/basketPageContent'
import ResponsiveDialog from '~/components/common/ResponsiveDialog'
import {useAddToBasketMutation} from '~/hooks/useBasket'
import useLocaleParams from '~/hooks/useLocaleParams'
import {Button} from '~/components/common/Button'
import {ProductVariant} from '~/gql/graphql'

type AddToBasketButtonBoxProps = {
    className?: string
    selectedVariant?: ProductVariant
}

const AddToBasketButtonBox = ({className, selectedVariant}: AddToBasketButtonBoxProps) => {
    const params = useLocaleParams()
    const t = useTranslations('basketButton')
    const {queryClient} = useRouteContext({from: '__root__'})

    const [showBasketSuccessDialog, setShowBasketSuccessDialog] = useState<boolean>(false)
    const hasSelectedVariant = !!selectedVariant
    const isOutOfStock = (selectedVariant?.stockQuantity ?? 0) <= 0

    const isReducedMotion = useReducedMotion()

    const {data: basketContent} = useQuery({
        ...basketPageContentQueryOptions(params.locale),
        enabled: showBasketSuccessDialog,
    })

    const {reward: triggerReward} = useReward('add-to-basket-reward', 'confetti', {
        position: 'fixed',
        spread: 30,
        startVelocity: 60,
        lifetime: 500,
    })

    const {mutate: addToBasket, isPending: isAddingToBasket} = useAddToBasketMutation({
        onSuccess: () => {
            if (isReducedMotion) {
                setShowBasketSuccessDialog(true)
            } else {
                triggerReward()
                setTimeout(() => {
                    setShowBasketSuccessDialog(true)
                }, 800)
            }
        },
        onMutate: () => {
            queryClient.prefetchQuery(basketPageContentQueryOptions())
        },
    })

    const closeDialog = useCallback(() => setShowBasketSuccessDialog(false), [])

    return (
        <>
            <div className={clsx(className)}>
                <div
                    aria-hidden="true"
                    id="add-to-basket-reward"
                    className="absolute left-[50%] w-1/2 bottom-0 max-h-screen h-full"
                />
                <Button
                    className={clsx(
                        'w-full',
                        (!hasSelectedVariant || isOutOfStock) && 'opacity-50'
                    )}
                    disabled={isAddingToBasket || !hasSelectedVariant}
                    onClick={() => {
                        hasSelectedVariant && addToBasket(selectedVariant)
                    }}
                    loader={{
                        label: t('loader'),
                        loading: isAddingToBasket,
                    }}
                >
                    {!hasSelectedVariant && t('selectSize')}
                    {hasSelectedVariant && isOutOfStock && t('outOfStock')}
                    {hasSelectedVariant && !isOutOfStock && t('addToBasket')}
                </Button>
            </div>
            <ResponsiveDialog
                open={showBasketSuccessDialog}
                onOpenChange={setShowBasketSuccessDialog}
                title={t('successDialog.title')}
                description={t('successDialog.description')}
                ssr={false}
            >
                <div className="pb-8 px-4 md:p-0">
                    {!!basketContent?.products?.length && (
                        <div className="mb-5">
                            <h2 className="mb-2">{t('successDialog.upselling.title')}</h2>
                            <Suspense fallback={<UpsellingProductsSkeletonLoader />}>
                                <UpsellingProducts
                                    products={basketContent.products}
                                    onSelect={closeDialog}
                                />
                            </Suspense>
                        </div>
                    )}
                    <div className="flex flex-col gap-4 mx-auto sm:gap-6 sm:flex-row md:py-4 md:max-w-sm">
                        <Button className="flex-1" onClick={closeDialog} variant="secondary">
                            {t('successDialog.continue')}
                        </Button>
                        <Button className="flex-1" as={Link} to="/basket" variant="primary">
                            {t('successDialog.openBasket')}
                        </Button>
                    </div>
                </div>
            </ResponsiveDialog>
        </>
    )
}

export default AddToBasketButtonBox
